import { FC, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import cn from "classnames";
import { createAirUpdateCardHook, useAirCardActions } from "features/AirCardActions";
import { useAirOpenedCardIsFollowButtonVisible } from "features/AirCardActions/model/hooks/useAirOpenedCardIsFollowButtonVisible";
import { AirlineList } from "features/AirOpenedCardTabs/ui/AirlineList";
import { EventList } from "features/AirOpenedCardTabs/ui/EventList";
import { cardToDeleteStore } from "features/CardToDelete";
import { EditTags } from "features/EditTags";
import { filteredCardsStore } from "features/Filter";
import { openedCardStore } from "features/OpenedCard/model/openedCard.store";
import { useAirUrlQueryHandler } from "features/QueryParamsManager";
import { useAirSearch } from "features/Search";
import { usePanelViews } from "features/shared/usePanelViews.hook";
import { tagsStore, useTagsContext } from "features/Tags";
import { isAirSavedCardModel } from "entities/AirSavedCard";
import { createAirSearchQueryParams, isAirSearchCardModel, useOpenedCardSearchQuery } from "entities/AirSearchCard";
import { appStore } from "entities/App";
import { authStore } from "entities/Auth";
import { Card, CardBase } from "entities/Card";
import { searchFilterStore } from "entities/SearchFilter";
import { subscriptionStore } from "entities/Subscription";
import { useAppSelector } from "shared/lib/redux";
import { t } from "shared/localization/localization";
import { Button, ButtonIcon } from "shared/ui/Button";
import { Preloader } from "shared/ui/Preloader";
import { Tabs } from "shared/ui/Tabs";
import { Tooltip } from "shared/ui/Tooltip";
import { TabIdList } from "./constants";
import styles from "./OpenedCardView.module.scss";

type OpenedCardLayerProps = {
  className?: string;
};

export const AirOpenedCardView: FC<OpenedCardLayerProps> = ({ className }) => {
  // useOpenedCardMarketplaceTrigger();

  const dispatch = useDispatch();
  const openedCard = useAppSelector(openedCardStore.selectors.openedCard);
  const isAuthenticated = useAppSelector(authStore.selectors.isAuthenticated);
  const { isCurrentViewOpenedCard, showMainView, clearViewHistory, currentView } = usePanelViews();
  const { refreshTags, allTags, activeTag, setActiveTag, getCardsByTags } = useTagsContext();
  const cardsByTag = useAppSelector(tagsStore.selectors.cardsByTags);
  const shipmentType = useAppSelector(searchFilterStore.selectors.shipment);

  const airCardsByTag = useMemo(() => {
    return cardsByTag.filter((card) => isAirSavedCardModel(card));
  }, [cardsByTag]);

  const airOpenedCard = useMemo(() => {
    if (isAirSavedCardModel(openedCard)) return openedCard;
    if (isAirSearchCardModel(openedCard)) return openedCard;
    return null;
  }, [openedCard]);

  const airSavedCard = useMemo(() => {
    if (isAirSavedCardModel(openedCard)) return openedCard;
    return null;
  }, [openedCard]);

  const airCarrierScac = useMemo(() => {
    if (isAirSavedCardModel(airOpenedCard)) return airOpenedCard.carrierScac;
    if (isAirSearchCardModel(airOpenedCard)) return airOpenedCard.iataCode;
    return null;
  }, [airOpenedCard]);

  const airCarrierName = useMemo(() => {
    if (isAirSavedCardModel(airOpenedCard)) return airOpenedCard.carrierName;
    if (isAirSearchCardModel(airOpenedCard)) return airOpenedCard.airlineName;
    return null;
  }, [airOpenedCard]);

  const { isAirSearchLoading } = useAirSearch({
    isAuthenticated,
  });

  const openedCardSearchRequestMeta = useOpenedCardSearchQuery({
    isAuthenticated,
    setSubscription: (subscription) => ({
      type: subscriptionStore.actions.setSubscription.type,
      payload: subscription,
    }),
    setActivePaymentModal: (activePaymentModal) => ({
      type: subscriptionStore.actions.setActivePaymentModal.type,
      payload: activePaymentModal,
    }),
    setAppIntegrationRequestLimitReached: (appIntegrationRequestLimitReached) => ({
      type: appStore.actions.setAppIntegrationRequestLimitReached.type,
      payload: appIntegrationRequestLimitReached,
    }),
    setWebAppRequestLimitReached: (webAppRequestLimitReached) => ({
      type: appStore.actions.setWebAppRequestLimitReached.type,
      payload: webAppRequestLimitReached,
    }),
  });
  const { searchResponse: cardData } = openedCardSearchRequestMeta;

  useAirUrlQueryHandler({
    openedCard: airOpenedCard,
  });

  const requestedFromUrl = useAppSelector(appStore.selectors.requestedFromUrl);

  const { isAirFollowButtonVisible } = useAirOpenedCardIsFollowButtonVisible({
    openedCard: airOpenedCard,
  });

  const { updateCard, shouldUpdateCard, isResolvingUpdatePossibility } = createAirUpdateCardHook({
    model: isAirSavedCardModel(airOpenedCard) ? airOpenedCard : null,
    isOpenedCardExist: !!airOpenedCard,
    openedCardUpdateAction: (response) => ({
      type: openedCardStore.actions.updateOpenCardAction.type,
      payload: response,
    }),
    updateFilteredCards: (response) => ({
      type: filteredCardsStore.actions.updateFilterCardAction.type,
      payload: response,
    }),
    updateCardByTagAction: (response) => ({
      type: tagsStore.actions.updateCardByTagAction.type,
      payload: response,
    }),
  })();
  const [tooltipLinkName, setTooltipLinkName] = useState(t("Delete"));

  const { deleteAirCardHandler, isAirCardDeleting, airCardFollow, isAirCardFollowLoading } = useAirCardActions({
    setCardToDeleteAction: (card) => ({
      type: cardToDeleteStore.actions.setCardToDeleteAction.type,
      payload: card,
    }),
    resetCardToDeleteAction: () => ({
      type: cardToDeleteStore.actions.resetCardToDeleteAction.type,
    }),
    removeFilteredCardAction: (card) => ({
      type: filteredCardsStore.actions.removeFilteredCardAction.type,
      payload: card,
    }),
    removeCardByTagAction: (card) => ({
      type: tagsStore.actions.removeCardByTagAction.type,
      payload: card,
    }),
    refreshTags,
  });

  const onFollowHandler = () => {
    if (isAirCardFollowLoading || !isAirSearchCardModel(airOpenedCard)) return;
    airCardFollow(airOpenedCard);
  };

  useEffect(() => {
    if (!requestedFromUrl) return;

    if (isAirSearchCardModel(airOpenedCard) && !!airOpenedCard?.rawData) {
      openedCardSearchRequestMeta.setSearchCache(airOpenedCard.rawData);
    }
  }, [airOpenedCard?.rawData]);

  useEffect(() => {
    if (
      !isCurrentViewOpenedCard ||
      openedCardSearchRequestMeta.isSearchLoading ||
      openedCardSearchRequestMeta.isSearchFetching
    ) {
      return;
    }

    if (isAirSearchCardModel(airOpenedCard)) {
      openedCardSearchRequestMeta.setSearchCache(airOpenedCard.rawData);
      return;
    }

    const reqParams = createAirSearchQueryParams({
      model: {
        reference: airOpenedCard?.reference,
      },
      requestType: "openedCard",
    });
    if (!reqParams) return;

    openedCardSearchRequestMeta.searchRequest(reqParams);
  }, [currentView, airOpenedCard?.rawData]);

  useEffect(() => {
    if (openedCardSearchRequestMeta.searchResponse?.requestLimitReached) {
      clearViewHistory();
      showMainView();
      dispatch(openedCardStore.actions.setOpenedCardAction(null));
    }
  }, [openedCardSearchRequestMeta.searchResponse?.rawData]);

  useEffect(
    () => () => {
      openedCardSearchRequestMeta.resetSearchQuery();
      dispatch(openedCardStore.actions.setOpenedCardAction(null));
      openedCardSearchRequestMeta.cancelSearchRequest();
    },
    []
  );

  const onUpdateHandler = () => {
    return isAirSavedCardModel(airOpenedCard) ? updateCard(airOpenedCard) : undefined;
  };

  const shouldUpdateCardHandler = (): boolean => {
    return isAirSavedCardModel(airOpenedCard) ? shouldUpdateCard() : false;
  };

  const onDeleteHandler = () => {
    if (!airOpenedCard) return;
    deleteAirCardHandler(airOpenedCard);
  };

  const copyUrlHandler = () => {
    const currentURL = window.location.href;
    navigator.clipboard.writeText(currentURL).then(() => {
      setTooltipLinkName(t("Copied"));
    });
  };

  if (
    openedCardSearchRequestMeta.isSearchLoading ||
    openedCardSearchRequestMeta.isSearchFetching ||
    isAirSearchLoading
  ) {
    return <Preloader className={cn(styles.loader, className)} />;
  }

  if (isAirSearchLoading) {
    return <Preloader className={cn(styles.loader, className)} />;
  }

  if (!openedCard) {
    return <div>{t("No data found!")}</div>;
  }

  return (
    <>
      <div className={styles.cardWrapper}>
        <Card
          shipmentType={shipmentType}
          className={cn(styles.airCard, className)}
          onDelete={onDeleteHandler}
          onFollow={isAirFollowButtonVisible && isAuthenticated ? onFollowHandler : undefined}
          onUpdate={onUpdateHandler}
          reference={openedCard.reference}
          isDeleteProcessing={isAirCardDeleting}
          isFollowLoading={isAirCardFollowLoading}
          canUpdate={shouldUpdateCardHandler()}
          isUpdating={isResolvingUpdatePossibility}
          progressLine={{
            percent: airOpenedCard?.progress ?? 0,
            status: "location-data",
            rotation: "horizontal",
          }}
          status={airOpenedCard?.status ?? null}
          pieces={airOpenedCard?.pieces}
          directionFrom={airOpenedCard?.directionFrom}
          directionTo={airOpenedCard?.directionTo}
          etd={airOpenedCard?.etd}
          eta={airOpenedCard?.eta}
          atd={airOpenedCard?.atd}
          ata={airOpenedCard?.ata}
          scac={airCarrierScac}
          carrierName={airCarrierName}
          tags={airSavedCard?.tags}
        />
      </div>

      <div className={cn(styles.cardsWrapper, styles.cardWrapper)}>
        <CardBase
          className={cn(styles["opened-card"], className)}
          onUpdate={onUpdateHandler}
          canUpdate={shouldUpdateCardHandler()}
          isUpdating={isResolvingUpdatePossibility}
          reference={openedCard.reference}
        >
          <CardBase.Row className={styles["tabs-row"]}>
            <Tabs className={styles["tabs"]} initialTabId={TabIdList.routes}>
              <Tabs.Row className={styles.tabsControls}>
                <Tabs.Group>
                  <Tabs.Control dataFor={TabIdList.routes}>
                    <Tabs.ControlLabel>{t("History")}</Tabs.ControlLabel>
                  </Tabs.Control>
                  <Tabs.Control dataFor={TabIdList.airline}>
                    <Tabs.ControlLabel>{t("Route")}</Tabs.ControlLabel>
                  </Tabs.Control>
                </Tabs.Group>
              </Tabs.Row>
              <Tabs.Body className={styles["tabs-body"]}>
                <Tabs.Panel data-tab={TabIdList.routes} className={styles["panel"]}>
                  <EventList data={cardData} />
                </Tabs.Panel>
                <Tabs.Panel data-tab={TabIdList.airline} className={cn(styles["panel"], styles["vessels-panel"])}>
                  <AirlineList data={cardData} />
                </Tabs.Panel>
              </Tabs.Body>
            </Tabs>
          </CardBase.Row>

          <CardBase.Row className={cn(styles["divider"], styles["bottom-actions"])}>
            {isAuthenticated && (
              <EditTags
                openedCard={openedCard}
                cardsByTag={airCardsByTag}
                allTags={allTags}
                setCardsByTagAction={(cards) => ({
                  type: tagsStore.actions.setCardsByTagAction.type,
                  payload: cards,
                })}
                refreshTags={refreshTags}
                getCardsByTags={getCardsByTags}
                activeTag={activeTag}
                setActiveTag={setActiveTag}
              />
            )}
            <div className={styles.actionsContainer}>
              {isAirFollowButtonVisible && (
                <Button
                  disabled={isAirCardFollowLoading}
                  variant="secondary"
                  size="tiny"
                  className={styles.follow}
                  onClick={onFollowHandler}
                  data-test-id={`card-action-follow-${openedCard?.reference}`}
                >
                  {t("Follow")}
                </Button>
              )}

              <Tooltip
                triggerElement={
                  <ButtonIcon
                    disabled={isAirCardDeleting}
                    icon="trash"
                    className={styles.delete}
                    onClick={onDeleteHandler}
                    data-test-id={`openedCard-action-delete-${openedCard?.reference}`}
                  />
                }
                offsetY={5}
              >
                <p className={styles.tooltipButtonsText}>{t("Delete")}</p>
              </Tooltip>

              <Tooltip
                triggerElement={
                  <ButtonIcon
                    icon="link"
                    className={styles.linkButton}
                    onClick={copyUrlHandler}
                    onMouseEnter={() => setTooltipLinkName(t("Copy"))}
                    data-test-id={`openedCard-action-copy-${openedCard?.reference}`}
                  />
                }
                offsetY={5}
              >
                <p className={styles.tooltipButtonsText}>{tooltipLinkName}</p>
              </Tooltip>
            </div>
          </CardBase.Row>
        </CardBase>
      </div>
    </>
  );
};

AirOpenedCardView.displayName = AirOpenedCardView.name;
